import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { 
  FiTrendingUp, FiUsers, FiDollarSign, FiActivity, 
  FiRefreshCw, FiDownload, FiArrowUp, FiArrowDown,
  FiPercent, FiCpu, FiUserCheck, FiUserX
} from 'react-icons/fi';

interface RevenueData {
  revenue: { mrr: number; arr: number; total_revenue: number; currency: string };
  users: { total: number; active: number; paying: number; lifetime: number; trialing: number; trial_conversion_rate: number };
  churn: { count_30d: number; rate: number };
  ai_usage: { this_month: number; total: number };
  by_plan: { starter: number; pro_trader: number; institutional_ai: number; enterprise: number };
  by_plan_revenue: { starter: number; pro_trader: number; institutional_ai: number; enterprise: number };
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatNumber = (value: number) => {
  return new Intl.NumberFormat('en-US').format(value);
};

const RevenueIntelligence: React.FC = () => {
  const [data, setData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result, error } = await supabase.rpc('admin_get_revenue_intelligence');
      if (error) throw error;
      
      // The RPC returns the data directly (revenue, users, churn, ai_usage, by_plan, by_plan_revenue)
      // Check for error response OR valid data structure
      if (result?.error) {
        console.error('RPC error:', result.error);
        return;
      }
      
      // If we have users data, it's valid
      if (result?.users) {
        setData(result as RevenueData);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error('Error fetching revenue data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const KPICard = ({ 
    title, value, subtitle, icon: Icon, trend, trendUp, color = 'purple'
  }: {
    title: string;
    value: string;
    subtitle?: string;
    icon: React.ElementType;
    trend?: string;
    trendUp?: boolean;
    color?: 'purple' | 'cyan' | 'green' | 'red' | 'orange';
  }) => {
    const colors = {
      purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
      cyan: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30',
      green: 'from-green-500/20 to-green-600/10 border-green-500/30',
      red: 'from-red-500/20 to-red-600/10 border-red-500/30',
      orange: 'from-orange-500/20 to-orange-600/10 border-orange-500/30',
    };
    const iconColors = {
      purple: 'text-purple-400',
      cyan: 'text-cyan-400',
      green: 'text-green-400',
      red: 'text-red-400',
      orange: 'text-orange-400',
    };

    return (
      <motion.div
        whileHover={{ scale: 1.02, y: -2 }}
        className={`relative overflow-hidden bg-gradient-to-br ${colors[color]} backdrop-blur-xl p-5 rounded-2xl border shadow-xl`}
      >
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-slate-400 text-xs font-medium uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold text-white">{loading ? '—' : value}</p>
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
          <div className={`p-2 rounded-lg bg-slate-800/50 ${iconColors[color]}`}>
            <Icon className="text-xl" />
          </div>
        </div>
        {trend && (
          <div className={`mt-3 flex items-center text-xs ${trendUp ? 'text-green-400' : 'text-red-400'}`}>
            {trendUp ? <FiArrowUp className="mr-1" /> : <FiArrowDown className="mr-1" />}
            <span>{trend}</span>
          </div>
        )}
      </motion.div>
    );
  };

  const PlanBreakdownCard = () => (
    <motion.div
      className="bg-gradient-to-br from-slate-800/80 to-slate-900/50 backdrop-blur-xl p-5 rounded-2xl border border-slate-700/50"
    >
      <h3 className="text-sm font-semibold text-white mb-4 flex items-center">
        <FiUsers className="mr-2 text-purple-400" />
        Revenue by Plan
      </h3>
      <div className="space-y-3">
        {[
          { name: 'Starter', users: data?.by_plan?.starter || 0, revenue: data?.by_plan_revenue?.starter || 0, color: 'bg-blue-500' },
          { name: 'Pro Trader', users: data?.by_plan?.pro_trader || 0, revenue: data?.by_plan_revenue?.pro_trader || 0, color: 'bg-purple-500' },
          { name: 'Institutional', users: data?.by_plan?.institutional_ai || 0, revenue: data?.by_plan_revenue?.institutional_ai || 0, color: 'bg-cyan-500' },
          { name: 'Enterprise', users: data?.by_plan?.enterprise || 0, revenue: data?.by_plan_revenue?.enterprise || 0, color: 'bg-emerald-500' },
        ].map((plan) => (
          <div key={plan.name} className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${plan.color}`} />
              <span className="text-sm text-slate-300">{plan.name}</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-xs text-slate-500">{plan.users} users</span>
              <span className="text-sm font-medium text-white">{formatCurrency(plan.revenue)}</span>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6" data-testid="revenue-intelligence">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center">
            <FiTrendingUp className="mr-2 text-purple-400" />
            Revenue Intelligence
          </h2>
          <p className="text-sm text-slate-400">
            {lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()}` : 'Loading...'}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchData}
            disabled={loading}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
          >
            <FiRefreshCw className={loading ? 'animate-spin' : ''} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-sm font-medium flex items-center space-x-2"
          >
            <FiDownload />
            <span>Export</span>
          </motion.button>
        </div>
      </div>

      {/* Primary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Monthly Recurring Revenue"
          value={formatCurrency(data?.revenue?.mrr || 0)}
          subtitle="MRR"
          icon={FiDollarSign}
          color="purple"
        />
        <KPICard
          title="Annual Run Rate"
          value={formatCurrency(data?.revenue?.arr || 0)}
          subtitle="ARR"
          icon={FiTrendingUp}
          color="cyan"
        />
        <KPICard
          title="Total Revenue"
          value={formatCurrency(data?.revenue?.total_revenue || 0)}
          subtitle="All-time"
          icon={FiDollarSign}
          color="green"
        />
        <KPICard
          title="Trial Conversion"
          value={`${data?.users?.trial_conversion_rate || 0}%`}
          subtitle="Trial → Paid"
          icon={FiPercent}
          color="cyan"
        />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <KPICard
          title="Total Users"
          value={formatNumber(data?.users?.total || 0)}
          icon={FiUsers}
          color="purple"
        />
        <KPICard
          title="Active Users"
          value={formatNumber(data?.users?.active || 0)}
          icon={FiUserCheck}
          color="green"
        />
        <KPICard
          title="Paying Users"
          value={formatNumber(data?.users?.paying || 0)}
          subtitle="Recurring"
          icon={FiDollarSign}
          color="cyan"
        />
        <KPICard
          title="Lifetime Users"
          value={formatNumber(data?.users?.lifetime || 0)}
          subtitle="One-time"
          icon={FiUserCheck}
          color="green"
        />
        <KPICard
          title="In Trial"
          value={formatNumber(data?.users?.trialing || 0)}
          icon={FiActivity}
          color="orange"
        />
        <KPICard
          title="Churn Rate"
          value={`${data?.churn?.rate || 0}%`}
          subtitle={`${data?.churn?.count_30d || 0} in 30d`}
          icon={FiUserX}
          color="red"
        />
      </div>

      {/* Plan Breakdown + AI Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PlanBreakdownCard />
        <motion.div
          className="bg-gradient-to-br from-slate-800/80 to-slate-900/50 backdrop-blur-xl p-5 rounded-2xl border border-slate-700/50"
        >
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center">
            <FiCpu className="mr-2 text-cyan-400" />
            AI Usage Metrics
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-slate-800/50 rounded-xl">
              <p className="text-xs text-slate-400 uppercase tracking-wider">This Month</p>
              <p className="text-2xl font-bold text-white mt-1">{formatNumber(data?.ai_usage?.this_month || 0)}</p>
              <p className="text-xs text-slate-500">analyses</p>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-xl">
              <p className="text-xs text-slate-400 uppercase tracking-wider">All Time</p>
              <p className="text-2xl font-bold text-white mt-1">{formatNumber(data?.ai_usage?.total || 0)}</p>
              <p className="text-xs text-slate-500">total analyses</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RevenueIntelligence;